import { type z } from "zod";

import { ValidationError } from "../errors";

// ============================================================
// Cross-cutting Discriminators
// ============================================================

/**
 * Which side of the bipartite graph a kind lives on. Used everywhere a
 * function takes "the entity that owns a kind" — error metadata,
 * extension classifiers, removal queues, schema diffs.
 */
export type KindEntity = "node" | "edge";

/**
 * Which physical surface an index targets. Vector indexes don't own a
 * kind directly (they back a per-`(kind, field)` typed embedding table
 * owned by the active `VectorStrategy`), but they participate in the same
 * materialization and diff pipelines, so they share this discriminator.
 *
 * `"system"` marks TypeGraph's own base-relation indexes
 * (`SYSTEM_INDEX_DECLARATIONS`) — graph-independent, but materialized and
 * status-tracked through the same pipeline; their status rows carry the
 * relation key (e.g. `"recordedNodes"`) in the `kind` column.
 */
export type IndexEntity = "node" | "edge" | "vector" | "system";

/**
 * Field-level null-check operations that round-trip through persisted
 * documents (unique-constraint where clauses, index where clauses,
 * graph-extension where clauses, IsNullPredicate). Consolidated so a
 * single rename or extension touches one site.
 */
export type NullCheckOp = "isNull" | "isNotNull";

// ============================================================
// Brand Keys for Nominal Typing
// ============================================================

/** Brand key for NodeType */
export const NODE_TYPE_BRAND = "__nodeType" as const;

/** Brand key for EdgeType */
export const EDGE_TYPE_BRAND = "__edgeType" as const;

/** Brand symbol for NodeId */
declare const __nodeId: unique symbol;

/** Brand symbol for EdgeId */
declare const __edgeId: unique symbol;

function assertNonEmptyId(value: string, path: string): void {
  if (value.length > 0) {
    return;
  }

  throw new ValidationError(
    `${path} must be a non-empty string.`,
    {
      issues: [
        {
          path,
          message: "Expected a non-empty string.",
        },
      ],
    },
    {
      suggestion:
        "Use a persisted node or edge id value, or let TypeGraph generate an id on create.",
    },
  );
}

// ============================================================
// Node Type
// ============================================================

/** A primitive JSON value, excluding arrays and objects. */
export type JsonScalar = null | string | number | boolean;

/**
 * Any JSON-serializable value.
 *
 * Mirrors the JSON wire format: primitives, arrays, and string-keyed objects.
 * `null` is included because the JSON spec requires it for "no value here";
 * this is the one place in the public API that uses `null` over `undefined`.
 */
export type JsonValue =
  JsonScalar | readonly JsonValue[] | Readonly<{ [key: string]: JsonValue }>;

/**
 * Consumer-owned per-kind annotations.
 *
 * Stable labels attached to a node or edge kind at definition time — UI hints,
 * audit policy, provenance pointers, tooling annotations.
 *
 * **Consumer-owned, fully.** TypeGraph never reads, validates, or interprets
 * values in this field. Consumers own the entire namespace; there are no
 * reserved keys or extension prefixes. Future library-owned per-kind state,
 * if needed, will use a separate sibling field rather than carving out keys
 * here.
 *
 * **Annotations participate in schema hashing.** Any change to an annotation
 * value (or adding/removing an annotation key) bumps the canonical schema
 * hash and is reported as a `safe`-severity diff by `getSchemaChanges`. If
 * you don't want versioning for a piece of state, do not put it here.
 *
 * Values must be JSON-serializable — `bigint`, `function`, `symbol`, `undefined`,
 * `Date`, and other class instances are rejected at definition time so they can
 * never silently break schema hashing or storage round-trips.
 */
export type KindAnnotations = Readonly<Record<string, JsonValue>>;

/**
 * Consumer-owned annotations for a graph as a whole.
 *
 * This is the schema-level counterpart to {@link KindAnnotations}: display
 * names, descriptions, capability declarations, and other JSON metadata that
 * describes the materialization itself rather than one node or edge kind.
 * Values participate in canonical schema hashing and annotation-only changes
 * are safe schema changes.
 */
export type GraphAnnotations = Readonly<Record<string, JsonValue>>;

/**
 * A node type definition.
 *
 * Created via `defineNode()`. Represents a type of node in the graph
 * with an associated Zod schema for properties.
 */
export type NodeType<
  K extends string = string,
  S extends z.ZodObject<z.ZodRawShape> = z.ZodObject<z.ZodRawShape>,
> = Readonly<{
  [NODE_TYPE_BRAND]: true;
  kind: K;
  schema: S;
  description: string | undefined;
  annotations: KindAnnotations | undefined;
}>;

/**
 * Branded node ID type.
 *
 * Prevents mixing IDs from different node types at compile time.
 */
export type NodeId<N extends NodeType> = string &
  Readonly<{
    [__nodeId]: N;
  }>;

/**
 * Brands a non-empty string as a {@link NodeId}.
 *
 * Use this when a persisted node id has round-tripped through untyped storage
 * or an external boundary and must be passed back to read/update/delete
 * surfaces such as `getById`, `getByIds`, `update`, or `delete`.
 * Write surfaces that mint or claim ids, such as `create({ id })` and
 * `upsertById`, intentionally accept plain strings.
 *
 * @throws {ValidationError} when `value` is empty.
 */
export function asNodeId<N extends NodeType = NodeType>(
  value: string,
): NodeId<N> {
  assertNonEmptyId(value, "asNodeId");
  return value as NodeId<N>;
}

/**
 * Infer the props type from a NodeType.
 */
export type NodeProps<N extends NodeType> = z.infer<N["schema"]>;

// ============================================================
// Edge Type
// ============================================================

/**
 * Target mapping from source kind name to allowed target node types.
 */
export type EdgeTargetMap = Readonly<Record<string, readonly NodeType[]>>;

/**
 * Valid edge target definitions: either a Cartesian array of target nodes,
 * or a source-to-target map for source-dependent endpoints.
 */
export type EdgeTargets = readonly NodeType[] | EdgeTargetMap;

/**
 * An edge type definition.
 *
 * Created via `defineEdge()`. Represents a type of edge in the graph
 * with an optional Zod schema for properties.
 *
 * Optionally includes `from` and `to` arrays or mappings that define the allowed
 * source and target node types (domain and range constraints).
 */
export type EdgeType<
  K extends string = string,
  S extends z.ZodObject<z.ZodRawShape> = z.ZodObject<z.ZodRawShape>,
  From extends readonly NodeType[] | undefined = undefined,
  To extends EdgeTargets | undefined = undefined,
> = Readonly<{
  [EDGE_TYPE_BRAND]: true;
  kind: K;
  schema: S;
  description: string | undefined;
  annotations: KindAnnotations | undefined;
  from: From;
  to: To;
}>;

/**
 * Base edge type for use in constraints - accepts any from/to configuration.
 */
export type AnyEdgeType = EdgeType<
  string,
  z.ZodObject<z.ZodRawShape>,
  readonly NodeType[] | undefined,
  EdgeTargets | undefined
>;

/**
 * An edge type that has both from and to constraints defined.
 * Can be used directly in defineGraph without an EdgeRegistration wrapper.
 */
export type EdgeTypeWithEndpoints = EdgeType<
  string,
  z.ZodObject<z.ZodRawShape>,
  readonly NodeType[],
  EdgeTargets
>;

/**
 * Branded edge ID type.
 *
 * Prevents mixing IDs from different edge types at compile time.
 */
export type EdgeId<E extends AnyEdgeType = AnyEdgeType> = string &
  Readonly<{
    [__edgeId]: E;
  }>;

/**
 * Brands a non-empty string as an {@link EdgeId}.
 *
 * Use this when a persisted edge id has round-tripped through untyped storage
 * or an external boundary and must be passed back to read/update/delete
 * surfaces such as `getById`, `getByIds`, `update`, or `delete`.
 * Write surfaces that mint ids intentionally accept plain strings.
 *
 * @throws {ValidationError} when `value` is empty.
 */
export function asEdgeId<E extends AnyEdgeType = AnyEdgeType>(
  value: string,
): EdgeId<E> {
  assertNonEmptyId(value, "asEdgeId");
  return value as EdgeId<E>;
}

/**
 * Infer the props type from an EdgeType.
 */
export type EdgeProps<E extends AnyEdgeType> = z.infer<E["schema"]>;

/**
 * The graph-local fields that identify an edge for durable matching.
 */
export type EdgeMatchIdentity<E extends AnyEdgeType = AnyEdgeType> = Readonly<{
  name: string;
  fields: readonly (keyof z.infer<E["schema"]> & string)[];
}>;

// ============================================================
// Configuration Types
// ============================================================

/**
 * Delete behaviors for nodes.
 */
export type DeleteBehavior = "restrict" | "cascade" | "disconnect";

/**
 * Edge cardinality constraints.
 */
export type Cardinality =
  | "many" // No constraint (default)
  | "one" // At most one edge of this kind from any source node
  | "unique" // At most one edge of this kind between any (source, target) pair
  | "oneActive"; // At most one edge with valid_to IS NULL from any source

/**
 * Endpoint existence modes for edge validation.
 */
export type EndpointExistence =
  | "notDeleted" // Endpoint deleted_at IS NULL (default)
  | "currentlyValid" // Endpoint not deleted AND temporally valid
  | "ever"; // Endpoint exists in any state

/**
 * Temporal query modes.
 */
export type TemporalMode =
  | "current" // Valid now AND not deleted
  | "asOf" // Valid at specific date AND not deleted
  | "includeEnded" // All validity periods AND not deleted
  | "includeTombstones"; // Everything including soft-deleted

/**
 * Uniqueness constraint scope.
 */
export type UniquenessScope =
  | "kind" // Unique within this exact kind only
  | "kindWithSubClasses"; // Unique across this kind and all subclasses

/**
 * Collation for uniqueness constraints.
 */
export type Collation = "binary" | "caseInsensitive";

// ============================================================
// Uniqueness Constraint
// ============================================================

/**
 * Uniqueness constraint definition.
 */
export type UniqueConstraint<
  S extends z.ZodObject<z.ZodRawShape> = z.ZodObject<z.ZodRawShape>,
> = Readonly<{
  name: string;
  fields: readonly (keyof z.infer<S> & string)[];
  where?: (
    props: UniqueConstraintPredicateBuilder<S>,
  ) => UniqueConstraintPredicate;
  scope: UniquenessScope;
  collation: Collation;
}>;

/**
 * Predicate builder for uniqueness constraint where clause.
 * Uses -? to make all fields required in the builder, even if optional in the schema.
 */
type UniqueConstraintPredicateBuilder<S extends z.ZodObject<z.ZodRawShape>> =
  Readonly<{
    [K in keyof z.infer<S>]-?: UniqueConstraintField;
  }>;

/**
 * Field operations for uniqueness constraint predicates.
 */
type UniqueConstraintField = Readonly<{
  isNull: () => UniqueConstraintPredicate;
  isNotNull: () => UniqueConstraintPredicate;
}>;

/**
 * A uniqueness constraint predicate (internal representation).
 */
type UniqueConstraintPredicate = Readonly<{
  __type: "unique_predicate";
  field: string;
  op: NullCheckOp;
}>;

// ============================================================
// Node Registration
// ============================================================

/**
 * Node registration in a graph definition.
 */
export type NodeRegistration<N extends NodeType = NodeType> = Readonly<{
  type: N;
  unique?: readonly UniqueConstraint<N["schema"]>[];
  onDelete?: DeleteBehavior;
}>;

// ============================================================
// Edge Registration
// ============================================================

/**
 * Edge registration in a graph definition.
 */
export type EdgeRegistration<
  E extends AnyEdgeType = AnyEdgeType,
  FromTypes extends NodeType = NodeType,
  ToTypes extends NodeType = NodeType,
  ToDef extends EdgeTargets = [NodeType] extends [ToTypes] ? EdgeTargets
  : readonly ToTypes[],
> = Readonly<{
  type: E;
  from: readonly FromTypes[];
  to: ToDef;
  cardinality?: Cardinality;
  endpointExistence?: EndpointExistence;
  matchIdentity?: EdgeMatchIdentity<E>;
}>;

/**
 * Base edge registration type for use in constraints - accepts any endpoint configuration.
 */
export type AnyEdgeRegistration = EdgeRegistration<
  AnyEdgeType,
  NodeType,
  NodeType,
  EdgeTargets
>;

// ============================================================
// Graph Defaults
// ============================================================

/**
 * Default settings for a graph.
 */
export type GraphDefaults = Readonly<{
  onNodeDelete?: DeleteBehavior;
  temporalMode?: TemporalMode;
}>;

// ============================================================
// Type Helpers
// ============================================================

/**
 * Checks if a value is a NodeType.
 */
export function isNodeType(value: unknown): value is NodeType {
  return (
    typeof value === "object" &&
    value !== null &&
    NODE_TYPE_BRAND in value &&
    (value as Record<string, unknown>)[NODE_TYPE_BRAND] === true
  );
}

/**
 * Checks if a value is an EdgeType.
 */
export function isEdgeType(value: unknown): value is AnyEdgeType {
  return (
    typeof value === "object" &&
    value !== null &&
    EDGE_TYPE_BRAND in value &&
    (value as Record<string, unknown>)[EDGE_TYPE_BRAND] === true
  );
}

/**
 * Checks if a value is an EdgeType with both from and to constraints defined.
 * Such edges can be used directly in defineGraph without an EdgeRegistration wrapper.
 */
export function isEdgeTypeWithEndpoints(
  value: unknown,
): value is EdgeTypeWithEndpoints {
  if (!isEdgeType(value)) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  if (
    !Array.isArray(candidate["from"]) ||
    candidate["from"].length === 0 ||
    typeof candidate["to"] !== "object" ||
    candidate["to"] === null
  ) {
    return false;
  }
  return Array.isArray(candidate["to"]) ?
      candidate["to"].length > 0
    : Object.keys(candidate["to"]).length > 0;
}
